import { useCallback, useEffect, useState } from 'react';
import { MessageSquare, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { comentariosService } from '../services/api';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';

/**
 * Panel de comentarios contextuales por artículo.
 * - Colapsable (arranca cerrado si no hay no-leídos, abierto si sí).
 * - Poll cada 20s si la pestaña está visible.
 * - Marca todos como leídos al abrir/actualizar mientras está abierto.
 */
export default function ArticleCommentsPanel({ articuloId }) {
  const [items, setItems] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [cargado, setCargado] = useState(false);

  const noLeidosCount = items.filter(c => !c.leido).length;

  const cargar = useCallback(async () => {
    if (!articuloId) return;
    try {
      const list = await comentariosService.listar(articuloId);
      setItems(list);
      setCargado(true);
      // Si al primer load hay no-leídos, abrir el panel automáticamente
      if (!cargado && list.some(c => !c.leido)) setAbierto(true);
    } catch { /* silent */ }
  }, [articuloId, cargado]);

  useEffect(() => { cargar(); }, [cargar]);

  // Poll solo cuando el artículo está identificado
  useVisibilityPolling(cargar, 20000, 60000, !!articuloId);

  // Al abrir, marcar como leídos (con pequeño delay para que el usuario los vea antes)
  useEffect(() => {
    if (abierto && noLeidosCount > 0) {
      const t = setTimeout(() => {
        comentariosService.marcarLeidos(articuloId)
          .then(() => setItems(prev => prev.map(c => ({ ...c, leido: true }))))
          .catch(() => {});
      }, 800);
      return () => clearTimeout(t);
    }
  }, [abierto, articuloId, noLeidosCount]);

  const enviar = async () => {
    if (!texto.trim() || enviando) return;
    setEnviando(true);
    try {
      const nuevo = await comentariosService.crear(articuloId, texto.trim());
      setItems(prev => [...prev, nuevo]);
      setTexto('');
    } catch (err) {
      alert('Error al enviar: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  // Enter envía; Shift+Enter podría añadir salto (por ahora usamos <input> simple)
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const fmtFecha = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('es-CO', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' });
  };

  if (!articuloId) return null;

  return (
    <div style={{
      background:'#fff', border:'1px solid #e5e7eb', borderRadius:8,
      marginTop:16, overflow:'hidden',
    }}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setAbierto(o => !o)}
        style={{
          display:'flex', alignItems:'center', gap:10, width:'100%',
          background:'#fafafa', border:'none', borderBottom: abierto ? '1px solid #e5e7eb' : 'none',
          padding:'12px 16px', cursor:'pointer', fontWeight:600, fontSize:14, color:'#1B4332',
        }}
      >
        <MessageSquare size={18} />
        Comentarios del equipo
        {items.length > 0 && (
          <span style={{ color:'#666', fontWeight:400 }}>({items.length})</span>
        )}
        {noLeidosCount > 0 && (
          <span style={{
            background:'#dc2626', color:'#fff', borderRadius:10, padding:'2px 8px',
            fontSize:11, fontWeight:700, marginLeft:4,
          }}>{noLeidosCount} nuevos</span>
        )}
        <span style={{ marginLeft:'auto' }}>
          {abierto ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {abierto && (
        <>
          {/* Hilo */}
          <div style={{ maxHeight:400, overflowY:'auto', padding:'12px 16px' }}>
            {items.length === 0 ? (
              <div style={{ color:'#888', fontSize:13, textAlign:'center', padding:20 }}>
                Sin comentarios todavía. Sé el primero en dejar una nota.
              </div>
            ) : (
              items.map(c => (
                <div key={c.id} style={{
                  marginBottom:12, padding:'8px 12px',
                  background: c.leido ? '#f9fafb' : '#fef3c7',
                  border:'1px solid ' + (c.leido ? '#eee' : '#fde68a'),
                  borderRadius:6, transition:'background 300ms',
                }}>
                  <div style={{ display:'flex', gap:10, marginBottom:4, fontSize:12 }}>
                    <strong style={{ color:'#1B4332' }}>{c.usuarioNombre}</strong>
                    <span style={{ color:'#888' }}>{fmtFecha(c.creadoEn)}</span>
                  </div>
                  <div style={{ fontSize:13, color:'#222', whiteSpace:'pre-wrap', lineHeight:1.4 }}>
                    {c.texto}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Composer — usamos <div> con handlers manuales (NO <form>) porque
              este componente vive dentro del <form> del ArticleEditorPage y
              HTML no permite forms anidados. Un submit interno acabaría
              disparando el guardado del artículo y recargando la página. */}
          <div style={{
            display:'flex', gap:8, padding:'10px 16px', borderTop:'1px solid #e5e7eb', background:'#fafafa',
          }}>
            <input
              type="text"
              value={texto}
              onChange={e => setTexto(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Escribe un comentario..."
              disabled={enviando}
              style={{
                flex:1, padding:'8px 12px', border:'1px solid #ddd',
                borderRadius:6, fontSize:13, outline:'none',
              }}
            />
            <button
              type="button"
              onClick={enviar}
              disabled={!texto.trim() || enviando}
              style={{
                background:'#1B4332', color:'#fff', border:'none',
                padding:'0 16px', borderRadius:6, fontSize:13, fontWeight:600,
                cursor: texto.trim() && !enviando ? 'pointer' : 'not-allowed',
                display:'flex', alignItems:'center', gap:6,
                opacity: texto.trim() && !enviando ? 1 : 0.5,
              }}
            >
              <Send size={14} />
              {enviando ? '...' : 'Enviar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
